import { ToastStatus } from '@/core/components/Toast/Toast';
import useMutation from '@/core/hooks/api/useMutation';
import useQuery from '@/core/hooks/api/useQuery';
import { APIError } from '@/core/lib/fetch';
import Api from '@/core/lib/new-architecture/api';
import type { Query } from '@/core/lib/new-architecture/query/types.query';
import { useRouterContext } from '@/core/lib/router/router.context';
import { useToastContext } from '@/core/lib/toast/toast.context';
import { useTranslationContext } from '@/core/lib/translation/translation.context';
import { StripeBodySchema } from '@/core/types/stripe';

const keys = {
  base: ['/api/stripe'] as const,
  details: (checkoutSessionId: string) => [...keys.base, checkoutSessionId] as const,
};

type AwaitedStripeCheckoutSession = Awaited<ReturnType<typeof Api.query.stripe.getCheckoutSession>>;

export const useCreateStripeCheckoutSession = () => {
  const toast = useToastContext();
  const { t } = useTranslationContext(['contribute']);

  return useMutation<Awaited<ReturnType<typeof Api.mutation.stripe.createCheckoutSession>>, APIError, StripeBodySchema>({
    mutationFn: async ({ customer_email, priceId, customer_uuid }) => Api.mutation.stripe.createCheckoutSession({ customer_email, priceId, customer_uuid }),
    onError: () => {
      toast?.open(t('errors.create-session-checkout', { ns: 'contribute' }), 2000, ToastStatus.DANGER);
    },
  });
};

export const useStripeCheckoutSession: Query<AwaitedStripeCheckoutSession> = () => {
  const { query } = useRouterContext();
  return useQuery({
    queryKey: keys.details(query.session_id),
    queryFn: () => Api.query.stripe.getCheckoutSession(query.session_id),
  });
};
